import type { CellContext } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import styled, { useTheme } from "styled-components";
import useSWR from "swr";
import {
  ButtonWithConfirmDialog,
  GhostButtonMediumV2,
  SecondaryButtonMediumV2,
} from "../../Buttons/Buttons";
import {
  DownloadIcon,
  EditIcon,
  LinkIcon,
  PlusIcon,
  XIcon,
} from "../../Icons/Icons";
import { SlideOut } from "../../SlideOut/SlideOut";
import { Table } from "../../Table/Table";
import { TableCellTwoLine } from "../../Table/TableCellTwoLine";
import { TablePlaceholder } from "../../../util/util";
import { endpoints } from "../../../endpoints";
import type { MethodsOfUseForm } from "../../../types/types";
import type {
  Assets,
  AssetsSummary,
  AttributeSchema,
  DocumentAttributeValue,
  GridItemProps,
  PIMProduct,
  SupportedAssetType,
} from "../../../types/types.PIM";
import { useStoreState } from "../../../util/util";
import {
  GetDocumentIcon,
  get_content_type,
} from "../../../pages/admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/AssetsUtil";
import { AddDocumentAttribute } from "./AddDocumentAttribute";
import { EditDocumentAttribute } from "./EditDocumentAttribute";
import { formatRequiredLabel } from "../../../pages/SharedPages/OrganizationPage/ProductsList/NewProductForm/utils";
import { strings } from "../../../util/strings";
import { TextMdRegular, TextXsRegular } from "../../Typography/TypographyV2";
import { useAuthContext } from "../../Auth";
import { DocumentPreview } from "../../../pages/admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/DocumentPreview";
import axios from "axios";

export type DocumentAttributeFormValue = DocumentAttributeValue & {
  value_id?: string;
};

const Card = styled.div<{ $hasError?: boolean }>`
  border: 1px solid
    ${({ theme, $hasError }) =>
      $hasError ? theme.errorColor : theme.secondaryBorder};
  border-radius: 8px;
  padding: 12px;
  width: 100%;
  max-width: 520px;
  td {
    max-width: 330px !important;
  }
`;

const CardHeader = styled.div`
  margin-bottom: 12px;
`;

const FieldErrorText = styled(TextXsRegular)`
  display: block;
  margin-top: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  button {
    padding: 0;
  }
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
`;

const ExternalLinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.primaryTextColor};
`;

type DocumentTableRow = DocumentAttributeFormValue;

export type DocumentAttributeProps = {
  attribute: AttributeSchema & { attribute_id?: string };
  methodsOfUseForm: MethodsOfUseForm;
  isDisabled?: boolean;
  isNotApplicable?: boolean;
  product?: PIMProduct;
  onCreateDocumentAttribute?: (
    docs: DocumentAttributeValue[],
    attribute: AttributeSchema & { attribute_id?: string }
  ) => void;
  uploadNewDocumentsOnly?: boolean;
  hideAccessibility?: boolean;
};

export function DocumentAttribute({
  attribute,
  methodsOfUseForm,
  isDisabled,
  isNotApplicable,
  product,
  onCreateDocumentAttribute,
  uploadNewDocumentsOnly = false,
  hideAccessibility = false,
}: DocumentAttributeProps) {
  const { t } = useTranslation();
  const {
    tenant_id,
    storefront_metadata: { enable_external_assets },
  } = useStoreState();
  const { roleIsGuest, roleIsSomeKindOfBuyer } = useAuthContext();
  const [showAddSlideout, setShowAddSlideout] = useState(false);
  const [editingAssetId, setEditingAssetId] = useState<string | null>(null);
  const [previewAssetId, setPreviewAssetId] = useState<string | null>(null);
  const [documents, setDocuments] = useState<DocumentAttributeFormValue[]>([]);
  const [isRegistered, setIsRegistered] = useState(false);

  const { data: previewAsset } = useSWR<Assets>(
    previewAssetId
      ? endpoints.v2_tenants_id_pim_assets_id(tenant_id, previewAssetId)
      : null,
    { revalidateOnFocus: false }
  );

  // RHF v6 returns a new object literal on every render, so keep a ref to
  // avoid including methodsOfUseForm in dependency arrays (which would cause
  // useEffect to re-run on every RHF internal state update, creating an
  // infinite loop: register() → setFormState() → re-render → new ref → effect re-runs)
  const methodsRef = useRef(methodsOfUseForm);
  methodsRef.current = methodsOfUseForm;

  const attributeId = useMemo(
    () => (attribute.attribute_id ? attribute.attribute_id : attribute.id),
    [attribute.attribute_id, attribute.id]
  );

  const initialDocuments = useMemo<DocumentAttributeFormValue[]>(
    () =>
      attribute.values?.reduce<DocumentAttributeFormValue[]>((acc, entry) => {
        const value = entry.value as DocumentAttributeValue | null;
        const {
          asset_name,
          asset_type,
          content_type,
          host,
          asset_mode,
          signed_url,
          preview_url,
          is_downloadable,
          visibility,
          is_generated,
        } = entry;
        if (!value?.asset_id) {
          return acc;
        }
        acc.push({
          asset_id: value.asset_id,
          display_name: asset_name ?? value.display_name,
          is_external:
            asset_mode !== undefined
              ? asset_mode === "external"
              : value.is_external ?? false,
          host_name: host?.name ?? value.host_name,
          asset_type,
          content_type,
          value_id: entry.id,
          signed_url: signed_url ?? value.signed_url,
          preview_url: preview_url ?? value.preview_url,
          is_downloadable: is_downloadable ?? value.is_downloadable,
          visibility: visibility ?? value.visibility,
          is_generated: is_generated ?? value.is_generated,
        });
        return acc;
      }, []) ?? [],
    [attribute.values]
  );

  const assetForPreview = useMemo<(Assets & GridItemProps) | null>(
    () =>
      previewAsset
        ? {
            ...previewAsset,
            can_edit: false,
            can_change_visibility: false,
            can_delete: false,
            can_external_download: roleIsGuest
              ? previewAsset.visibility === "public"
              : !roleIsSomeKindOfBuyer ||
                previewAsset.visibility !== "internal",
            can_view_details: true,
          }
        : null,
    [previewAsset, roleIsGuest, roleIsSomeKindOfBuyer]
  );

  const canAddMore = !isDisabled && !isNotApplicable;
  const isRequired = !!attribute.is_required && canAddMore;
  const requiredMessage = strings(t).thisIsARequiredField;
  const fieldError = methodsOfUseForm.errors[attributeId];
  const hasValidationError =
    methodsOfUseForm.formState.submitCount > 0 &&
    Boolean(fieldError?.message || fieldError?.type);
  const validationErrorMessage =
    !fieldError?.message || fieldError.message === "Required"
      ? requiredMessage
      : String(fieldError.message);

  const syncFormValue = useCallback(
    (nextDocuments: DocumentAttributeFormValue[]) => {
      setDocuments(nextDocuments);
      methodsRef.current.setValue(
        attributeId,
        nextDocuments.map(({ asset_id, value_id }) => ({
          asset_id,
          ...(value_id ? { value_id } : {}),
        })),
        { shouldValidate: methodsRef.current.formState.submitCount > 0 }
      );
      onCreateDocumentAttribute?.(nextDocuments, attribute);
    },
    [attribute, attributeId, onCreateDocumentAttribute]
  );

  useEffect(() => {
    methodsRef.current.register(attributeId, {
      required: isRequired ? requiredMessage : false,
      validate: (value: unknown) => {
        if (!isRequired) {
          return true;
        }
        if (Array.isArray(value) && value.length > 0) {
          return true;
        }
        return requiredMessage;
      },
    });
    setIsRegistered(true);
    if (!isRegistered) {
      syncFormValue(initialDocuments);
    }
  }, [
    attributeId,
    initialDocuments,
    isRegistered,
    isRequired,
    requiredMessage,
    syncFormValue,
  ]);

  useEffect(() => {
    if (isNotApplicable) {
      syncFormValue([]);
    } else if (!isNotApplicable && documents.length === 0) {
      syncFormValue(initialDocuments);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Don't want to include syncFormValue or initialDocuments in deps, as that causes infinite loop
  }, [isNotApplicable]);

  const handleAddDocuments = useCallback(
    (newDocs: DocumentAttributeFormValue[]) => {
      const existingIds = new Set(documents.map((doc) => doc.asset_id));
      const additions = newDocs.filter((doc) => !existingIds.has(doc.asset_id));
      syncFormValue([...documents, ...additions]);
      setShowAddSlideout(false);
    },
    [documents, syncFormValue]
  );

  const handleEditComplete = useCallback(async () => {
    const { data: updatedAsset } = await axios.get<AssetsSummary>(
      endpoints.v2_tenants_id_pim_assets_id_summary(tenant_id, editingAssetId!)
    );
    const nextDocuments: DocumentAttributeFormValue[] = documents.map((doc) => {
      if (doc.asset_id === editingAssetId) {
        return {
          ...doc,
          display_name: updatedAsset.name,
          asset_type: updatedAsset.asset_type,
          content_type: updatedAsset.content_type ?? undefined,
          host_name: updatedAsset.host?.name ?? doc.host_name,
          is_external:
            updatedAsset.asset_mode !== undefined
              ? updatedAsset.asset_mode === "external"
              : doc.is_external,
          signed_url: updatedAsset.signed_url,
          preview_url: updatedAsset.preview_url,
          is_generated: updatedAsset.is_generated,
          is_downloadable: updatedAsset.is_downloadable,
          visibility: updatedAsset.visibility,
        };
      }
      return doc;
    });
    syncFormValue(nextDocuments);
    setEditingAssetId(null);
  }, [documents, editingAssetId, syncFormValue, tenant_id]);

  const handleRemove = useCallback(
    (assetId: string) => {
      syncFormValue(documents.filter((doc) => doc.asset_id !== assetId));
    },
    [documents, syncFormValue]
  );

  /**
   * When external assets are disabled, editing an externally hosted asset
   * silently resets its Asset Host to Agilis (the edit form hides that field
   * and submits the default host), so editing must be blocked instead.
   */
  const isExternalAssetEditDisabled = useCallback(
    (doc: DocumentAttributeFormValue) =>
      !enable_external_assets && !!doc.is_external,
    [enable_external_assets]
  );

  const columns = useMemo(
    () => [
      {
        header: t("Asset Name"),
        accessorKey: "asset_id",
        id: "asset_id",
        cell: ({ row }: CellContext<DocumentTableRow, string>) => {
          const doc = row.original;
          return (
            <NameCell
              onClick={
                !doc.is_external
                  ? () => setPreviewAssetId(doc.asset_id)
                  : undefined
              }
              style={!doc.is_external ? { cursor: "pointer" } : undefined}
            >
              <div>
                {doc.is_external ? (
                  <LinkIcon width={20} height={20} />
                ) : (
                  <GetDocumentIcon
                    asset_type={
                      (doc.asset_type as SupportedAssetType) ?? "document"
                    }
                    file_type={get_content_type(
                      doc.content_type ?? "application/pdf"
                    )}
                  />
                )}
              </div>
              <TableCellTwoLine
                primaryText={doc.display_name || t("Document")}
                secondaryText={doc.host_name ?? t("Agilis")}
              />
            </NameCell>
          );
        },
      },
      {
        header: "",
        id: "actions",
        accessorKey: "actions",
        cell: ({ row }: CellContext<DocumentTableRow, unknown>) => (
          <DocumentAttributeActions
            assetId={row.original.asset_id}
            isExternal={row.original.is_external}
            signedUrl={row.original.signed_url}
            isGenerated={row.original.is_generated}
            onRemove={() => handleRemove(row.original.asset_id)}
            onEdit={() => setEditingAssetId(row.original.asset_id)}
            isDisabled={isDisabled || isNotApplicable}
            isEditDisabledByExternalAssets={isExternalAssetEditDisabled(
              row.original
            )}
          />
        ),
      },
    ],
    [handleRemove, isDisabled, isExternalAssetEditDisabled, isNotApplicable, t]
  );

  return (
    <>
      <Card $hasError={hasValidationError}>
        <CardHeader>
          <TextMdRegular color={hasValidationError ? "error" : "primary"}>
            {formatRequiredLabel(attribute, t)}
          </TextMdRegular>
          {hasValidationError && (
            <FieldErrorText
              color="error"
              className="errorMessage"
              data-testid={`document-attribute-error-${attributeId}`}
            >
              {validationErrorMessage}
            </FieldErrorText>
          )}
        </CardHeader>
        <Table
          columns={columns}
          data={documents}
          isLoading={false}
          error={undefined}
          Placeholder={<TablePlaceholder message={t("No items to show")} />}
        />
        {canAddMore && (
          <SecondaryButtonMediumV2
            type="button"
            testid={`add-document-${attributeId}`}
            onClick={() => setShowAddSlideout(true)}
            style={{ marginTop: 12 }}
          >
            <PlusIcon />
            {t("Add")}
          </SecondaryButtonMediumV2>
        )}
      </Card>
      <SlideOut
        title={t("Add Document")}
        show={showAddSlideout}
        closeFlyout={() => setShowAddSlideout(false)}
      >
        <AddDocumentAttribute
          product={product}
          onAddDocuments={handleAddDocuments}
          onCancel={() => setShowAddSlideout(false)}
          uploadNewDocumentsOnly={uploadNewDocumentsOnly}
          hideAccessibility={hideAccessibility}
        />
      </SlideOut>
      <SlideOut
        title={t("Edit Document")}
        show={Boolean(editingAssetId)}
        closeFlyout={() => setEditingAssetId(null)}
      >
        {editingAssetId ? (
          <EditDocumentAttribute
            key={editingAssetId}
            assetId={editingAssetId}
            product={product}
            onComplete={handleEditComplete}
            onCancel={() => setEditingAssetId(null)}
            hideAccessibility={hideAccessibility}
          />
        ) : null}
      </SlideOut>
      {assetForPreview && (
        <DocumentPreview
          assets={[assetForPreview]}
          selected_asset_idx={0}
          onCancelPreview={() => setPreviewAssetId(null)}
        />
      )}
    </>
  );
}

function DocumentAttributeActions({
  assetId,
  isExternal,
  signedUrl,
  isGenerated,
  onRemove,
  onEdit,
  isDisabled,
  isEditDisabledByExternalAssets,
}: {
  assetId: string;
  isExternal?: boolean;
  signedUrl?: string;
  isGenerated?: boolean;
  onRemove: () => void;
  onEdit: () => void;
  isDisabled?: boolean;
  isEditDisabledByExternalAssets?: boolean;
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  // For external assets, signed_url already resolves to the external
  // document's own URL (see Asset.signed_url on the backend), so no
  // separate document_url/asset_mode lookup is needed here.
  const externalUrl = isExternal ? signedUrl : undefined;

  return (
    <ActionsCell>
      {externalUrl ? (
        <ExternalLinkButton
          href={externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`open-external-document-${assetId}`}
        >
          <LinkIcon />
        </ExternalLinkButton>
      ) : null}
      {!isExternal && signedUrl ? (
        <GhostButtonMediumV2
          type="button"
          testid={`download-document-${assetId}`}
          onClick={() => window.open(signedUrl, "_blank")}
        >
          <DownloadIcon width={16} height={16} />
        </GhostButtonMediumV2>
      ) : null}
      <ButtonWithConfirmDialog
        confirmMessage={t("Are you sure you want to remove this document?")}
        handleConfirm={onRemove}
        disabled={isDisabled}
        testid={`remove-document-${assetId}`}
        Button={({ onClick, disabled: btnDisabled }) => (
          <GhostButtonMediumV2
            type="button"
            onClick={onClick}
            disabled={btnDisabled}
          >
            <XIcon
              fill={theme.resolve("color_button_icon_destructive_enabled")}
              width={16}
              height={16}
            />
          </GhostButtonMediumV2>
        )}
        confirmBtnLabel={t("Remove")}
        confirmBtnType="destructive"
      />
      {!isDisabled && !isGenerated ? (
        <div
          data-for={`edit-document-disabled-tooltip-${assetId}`}
          data-tip={
            isEditDisabledByExternalAssets
              ? t(
                  "This asset is hosted externally and cannot be edited while external assets are disabled."
                )
              : ""
          }
        >
          <GhostButtonMediumV2
            type="button"
            testid={`edit-document-${assetId}`}
            onClick={onEdit}
            disabled={isEditDisabledByExternalAssets}
          >
            <EditIcon width={16} height={16} />
          </GhostButtonMediumV2>
          <ReactTooltip id={`edit-document-disabled-tooltip-${assetId}`} />
        </div>
      ) : null}
    </ActionsCell>
  );
}
